import {
  X,
  Bank,
  Eye,
  EyeSlash,
  ArrowSquareOut,
  DownloadSimple,
  Info,
  CaretDown,
  CaretUp,
} from '@phosphor-icons/react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipLoader } from 'react-spinners';
import { Tooltip } from 'antd';
import toast from 'react-hot-toast';
import useApi from '../../../../../../utils/AxiosWrapper';
import { unwrapApiResponse } from '../../../../../../utils/unwrapApiResponse';
import { useUser } from '../../../../../../utils/UserContext';
import { InternalBankAccount } from '../../../../../../types/dbTypes';
import { ACH_WIRE_TOOLTIP } from '../../../../../../constants/bankingCopy';

interface DirectDepositInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export const DirectDepositInstructionsModal: React.FC<
  DirectDepositInstructionsModalProps
> = ({ isOpen, onClose, onComplete }) => {
  const navigate = useNavigate();
  const { userId } = useUser();
  const { callProtectedApi } = useApi();
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [showWireAccountNumber, setShowWireAccountNumber] = useState(false);
  const [showWireDetails, setShowWireDetails] = useState(false);
  const [downloadingVoidedCheck, setDownloadingVoidedCheck] = useState(false);

  const fallbackRouting = '053101561';

  const { data: accountInfo, isLoading: loadingAccountInfo } =
    useQuery<InternalBankAccount>({
      queryKey: ['accountInfo', userId],
      queryFn: async () => {
        const response = await callProtectedApi(
          `/internalBankAccounts/${userId ?? ''}`,
          'get',
          {},
          'Error occurred while fetching your account info.'
        );
        return unwrapApiResponse<{ internalBankAccount: InternalBankAccount }>(
          response,
          'Error occurred while fetching your account info.'
        ).internalBankAccount;
      },
      enabled: !!userId && isOpen,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

  const maskAccountNumber = (accountNumber: string) => {
    if (!accountNumber) return '';
    return (
      '*'.repeat(Math.max(0, accountNumber.length - 4)) +
      accountNumber.slice(-4)
    );
  };

  const hasWireInfo =
    !!accountInfo?.wire_account_number && !!accountInfo?.wire_routing_number;

  const downloadVoidedCheck = async () => {
    if (!accountInfo?.account_id) {
      toast.error(
        'Unable to download voided check. Account information is missing.'
      );
      return;
    }

    setDownloadingVoidedCheck(true);
    try {
      const response = await callProtectedApi(
        `/documents/display/voidedCheck/${accountInfo.account_id}`,
        'post',
        { document_count: 1 },
        'Error occurred while downloading your voided check.'
      );

      const presignedUrl = response?.data?.data?.document_urls?.[0];
      if (!presignedUrl) {
        throw new Error('Voided check not available.');
      }

      const fileResponse = await fetch(presignedUrl);
      if (!fileResponse.ok) {
        throw new Error('Failed to download file');
      }

      const contentType =
        fileResponse.headers.get('content-type') || 'application/pdf';
      const extension = contentType.includes('pdf')
        ? 'pdf'
        : contentType.split('/')[1] || 'pdf';

      const blob = await fileResponse.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `TACH Account Voided Check.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('Voided check downloaded successfully.', {
        duration: 5000,
      });
    } catch (error: any) {
      console.error('Error downloading voided check:', error);
      toast.error('Failed to download voided check. Please contact support.');
    } finally {
      setDownloadingVoidedCheck(false);
    }
  };

  const handleViewAccountInfo = () => {
    navigate('/dashboard/banking/overview');
    if (onComplete) {
      onComplete();
    }
    onClose();
  };

  const renderContent = () => (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="flex h-[90%] max-h-[800px] w-[90%] flex-col overflow-hidden rounded-[4px] bg-white md:w-[600px] md:max-w-[600px]">
        {/* Fixed Header */}
        <div className="flex h-[56px] min-h-[56px] w-full flex-row items-center justify-between border-b border-bordergray p-4 align-middle">
          <h2 className="text-[16px] font-[600] text-primary">
            Set Up Direct Deposit
          </h2>
          <X
            className="hover:cursor-pointer hover:opacity-80"
            onClick={onClose}
            weight="regular"
            size={24}
          />
        </div>

        {/* Scrollable Content */}
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden p-6">
          {/* Intro */}
          <div className="rounded-lg border border-bordergray bg-gray-50 p-4">
            <p className="text-sm text-gray-700">
              To get paid directly into your TACH Business account, give the{' '}
              <span className="font-semibold">ACH details</span> below to your
              factoring company (or whoever pays you) and ask them to send your
              settlements there. Direct deposit always uses ACH.
            </p>
          </div>

          {/* Account Details Card */}
          <div className="flex flex-col gap-4 rounded-lg border border-bordergray p-4">
            <div className="flex items-center gap-2">
              <Bank weight="regular" className="text-2xl text-graysecondary" />
              <h3 className="text-[16px] font-semibold text-primary">
                Your TACH Account Details
              </h3>
            </div>

            {loadingAccountInfo ? (
              <div className="flex items-center justify-center py-6">
                <ClipLoader color="#1D213B" size={40} />
              </div>
            ) : accountInfo ? (
              <div className="flex flex-col gap-4">
                {/* Bank Name */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium uppercase tracking-wide text-[#858897]">
                    Bank Name
                  </span>
                  <span className="text-sm font-medium text-primary">
                    Wells Fargo Bank, N.A.
                  </span>
                </div>

                {/* Bank Address */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium uppercase tracking-wide text-[#858897]">
                    Bank Address
                  </span>
                  <span className="text-sm font-medium text-primary">
                    165 E Franklin Street, Chapel Hill, NC 27514
                  </span>
                </div>

                {/* Standard (ACH) Section — primary, always expanded and
                    visually highlighted. Direct deposit is ACH, so this is the
                    only set most customers need. */}
                <div className="rounded-md border border-accent bg-accent/10 p-3">
                  <div className="mb-2 flex items-center gap-1.5">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-primary">
                      Use These for Direct Deposit (ACH)
                    </h4>
                    <Tooltip
                      title={ACH_WIRE_TOOLTIP}
                      placement="top"
                      overlayStyle={{ maxWidth: 260, fontSize: 12 }}
                    >
                      <Info size={14} className="cursor-help text-[#858897]" />
                    </Tooltip>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-[#858897]">
                          Account Number
                        </span>
                        <span className="font-mono text-sm text-primary">
                          {showAccountNumber
                            ? (accountInfo.account_number ?? '')
                            : maskAccountNumber(
                                accountInfo.account_number ?? ''
                              )}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setShowAccountNumber(!showAccountNumber);
                        }}
                        className="p-1 text-[#858897] hover:text-primary"
                        aria-label={
                          showAccountNumber
                            ? 'Hide account number'
                            : 'Show account number'
                        }
                      >
                        {showAccountNumber ? (
                          <EyeSlash size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs text-[#858897]">
                        Routing Number
                      </span>
                      <span className="font-mono text-sm text-primary">
                        {!accountInfo.routing_number ||
                        accountInfo.routing_number.length === 0
                          ? fallbackRouting
                          : accountInfo.routing_number}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Wire Section — secondary, collapsed by default. Only surfaced
                    for the rare payer that specifically requests a wire, so it
                    can't be mistaken for the direct-deposit details above. */}
                {hasWireInfo && (
                  <div className="rounded-md border border-bordergray">
                    <button
                      onClick={() => {
                        const next = !showWireDetails;
                        setShowWireDetails(next);
                        // Re-mask the wire number when collapsing, so re-expanding
                        // doesn't reveal it without an explicit tap.
                        if (!next) setShowWireAccountNumber(false);
                      }}
                      aria-expanded={showWireDetails}
                      className="flex w-full items-center justify-between p-3 text-left"
                    >
                      <span className="text-xs font-medium text-[#858897]">
                        Only sending a wire? Show wire details
                      </span>
                      {showWireDetails ? (
                        <CaretUp size={16} className="text-[#858897]" />
                      ) : (
                        <CaretDown size={16} className="text-[#858897]" />
                      )}
                    </button>
                    {showWireDetails && (
                      <div className="border-t border-bordergray p-3">
                        <p className="mb-2 text-xs text-[#858897]">
                          Wire is for same-day settlement and is rarely needed
                          for direct deposit. Only use these if your payer
                          specifically asks for a wire.
                        </p>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs text-[#858897]">
                                Wire Account Number
                              </span>
                              <span className="font-mono text-sm text-primary">
                                {showWireAccountNumber
                                  ? (accountInfo.wire_account_number ?? '')
                                  : maskAccountNumber(
                                      accountInfo.wire_account_number ?? ''
                                    )}
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                setShowWireAccountNumber(!showWireAccountNumber);
                              }}
                              className="p-1 text-[#858897] hover:text-primary"
                              aria-label={
                                showWireAccountNumber
                                  ? 'Hide wire account number'
                                  : 'Show wire account number'
                              }
                            >
                              {showWireAccountNumber ? (
                                <EyeSlash size={18} />
                              ) : (
                                <Eye size={18} />
                              )}
                            </button>
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs text-[#858897]">
                              Wire Routing Number
                            </span>
                            <span className="font-mono text-sm text-primary">
                              {accountInfo.wire_routing_number}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Unable to load account details. You can find them in your
                Banking Overview tab.
              </p>
            )}
          </div>

          {/* Steps Section */}
          <div className="flex flex-col gap-3 rounded-lg border border-bordergray p-4">
            <h4 className="text-sm font-semibold text-primary">
              Here's what to do:
            </h4>
            <ol className="list-inside list-decimal space-y-2 text-sm text-graysecondary">
              <li>
                Contact your factoring company (or the broker/shipper who pays
                you).
              </li>
              <li>
                Provide them with the bank name and your{' '}
                <span className="font-semibold">
                  ACH account and routing number
                </span>{' '}
                shown above.
              </li>
              <li>
                Ask them to update your direct deposit so payments are sent to
                your new TACH Business account.
              </li>
              <li>
                Confirm the change has been made — your next settlement should
                arrive in your TACH account.
              </li>
            </ol>
          </div>

          {/* Reminder Callout */}
          <div className="rounded-lg bg-blue-50 p-4">
            <p className="text-sm text-blue-800">
              You can always find your account and routing numbers in the{' '}
              <button
                onClick={handleViewAccountInfo}
                className="inline-flex items-center gap-1 font-medium underline hover:text-blue-900"
              >
                Banking Overview
                <ArrowSquareOut size={14} />
              </button>{' '}
              tab.
            </p>
          </div>

          {/* Benefits Message */}
          <div className="rounded-lg bg-blue-50 p-4">
            <p className="text-sm text-blue-800">
              Once your factoring company has updated your payment instructions,
              all future settlements will deposit directly into TACH — unlocking
              intelligent budgeting, expense management, TACH business cards,
              and more.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            {accountInfo?.account_id && (
              <Tooltip
                title="Your voided check contains ACH details only — don't use it to set up a wire."
                placement="top"
                overlayStyle={{ maxWidth: 260, fontSize: 12 }}
              >
                <button
                  onClick={downloadVoidedCheck}
                  disabled={downloadingVoidedCheck}
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-bordergray bg-white px-4 py-3 text-sm font-medium text-primary hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {downloadingVoidedCheck ? (
                    <ClipLoader color="#1D213B" size={18} />
                  ) : (
                    <DownloadSimple weight="regular" size={20} />
                  )}
                  Download ACH Voided Check
                </button>
              </Tooltip>
            )}
            <button
              onClick={handleViewAccountInfo}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-accent px-4 py-3 text-sm font-medium text-primary hover:bg-opacity-90"
            >
              <Bank weight="regular" size={20} />
              View Account Info
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return <>{renderContent()}</>;
};

export default DirectDepositInstructionsModal;
